import { inject, Injectable } from '@angular/core'
import { TRANSCRIBER_PERMISSIONS, UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { unwrapData } from '@ftr/foundation'
import { AppPaths, OrderPaths } from '@ftr/routing-paths'
import { FeatureFlagState } from '@ftr/ui-feature-flags'
import { HelpMenuItemsProvider } from '@ftr/ui-help'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { combineLatest, map, Observable } from 'rxjs'
import { toSideNavItem } from '../help-support'
import { getGlobalAdminNavItem, SideNavItem } from '../side-nav-item'
import { SideNavProviderResult } from '../side-nav-provider-result'

const DEFAULT_NAV_ITEMS = {
  'transcript-jobs': {
    type: 'LINK',
    title: 'Transcript Jobs',
    path: [`/${AppPaths.Jobs}`],
    icon: 'TranscriptJobs',
  } as SideNavItem,
  'my-orders': {
    type: 'LINK',
    title: 'My Orders',
    path: [`/${AppPaths.Orders}`],
    icon: 'MyOrders',
  } as SideNavItem,
  'shared-recordings': {
    type: 'LINK',
    title: 'Shared Recordings',
    path: [`/${AppPaths.SharedRecordings}`],
    icon: 'SharedRecordings',
  } as SideNavItem,
  'order-audio': {
    type: 'LINK',
    title: 'Audio',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Audio],
    icon: 'AudioOrder',
    tooltipHint: 'Place an Audio Order',
  } as SideNavItem,
  'order-real-time': {
    type: 'LINK',
    title: 'Live',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.RealTime],
    icon: 'RealtimeOrder',
    tooltipHint: 'Place a Live Order',
  } as SideNavItem,
  'order-transcript': {
    type: 'LINK',
    title: 'Transcript',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Transcript],
    icon: 'TranscriptOrder',
    tooltipHint: 'Place a Transcript Order',
  } as SideNavItem,
  'select-court-system': {
    type: 'LINK',
    title: 'Select a Court System',
    path: ['/court-system'],
    icon: 'Gavel',
  } as SideNavItem,
}

@Injectable({
  providedIn: 'root',
})
export class ExternalUserNavItemsProvider {
  private readonly store = inject(Store)
  private readonly helpMenuItemsProvider = inject(HelpMenuItemsProvider)

  provide(): Observable<SideNavProviderResult> {
    return combineLatest([
      this.store.select(UserState.courtSystems).pipe(unwrapData()),
      this.store.select(UserState.currentCourtSystem),
      this.store
        .select(UserState.hasPermissionInAnyCourtSystem)
        .pipe(map(fn => fn(UserGroupPermissionId.TranscribeRecordings))),
      this.store.select(UserState.isGlobalAdmin),
      this.store.select(UserState.hasOnlyPermissionsInAllCourtSystems).pipe(map(fn => fn(...TRANSCRIBER_PERMISSIONS))),
      this.store.select(FeatureFlagState.featureFlag('public-navigation')),
    ]).pipe(
      map(
        ([
          courtSystems,
          currentCourtSystem,
          hasTranscriberPermission,
          isGlobalAdmin,
          hasOnlyTranscriberPermissions,
          publicNavigationEnabled,
        ]) => {
          const helpSupportFlyoutItems = this.helpMenuItemsProvider.provide(currentCourtSystem?.id)
          const selectCourtSystemItem: SideNavItem = {
            ...DEFAULT_NAV_ITEMS['select-court-system'],
            title: publicNavigationEnabled ? 'Enter a Court System' : 'Select a Court System',
          }

          const hasCourtSystems = (courtSystems?.length ?? 0) > 0
          const footerItems = [
            ...(isGlobalAdmin ? [getGlobalAdminNavItem()] : []),
            ...(hasCourtSystems && !hasOnlyTranscriberPermissions ? [selectCourtSystemItem] : []),
            {
              type: 'BRANCH' as const,
              title: 'Help & Support',
              icon: 'HelpCircleSolid',
              displayMode: 'OVERLAY',
              children: helpSupportFlyoutItems().map(toSideNavItem),
            } as SideNavItem,
          ]

          return {
            navGroups: [
              ...(hasTranscriberPermission ? [{ items: [DEFAULT_NAV_ITEMS['transcript-jobs']] }] : []),
              {
                title: 'My content',
                items: [DEFAULT_NAV_ITEMS['my-orders'], DEFAULT_NAV_ITEMS['shared-recordings']],
              },
              {
                title: 'Place an order',
                items: [
                  DEFAULT_NAV_ITEMS['order-audio'],
                  DEFAULT_NAV_ITEMS['order-real-time'],
                  DEFAULT_NAV_ITEMS['order-transcript'],
                ],
              },
            ],
            footerItems,
          }
        },
      ),
    )
  }
}
